import { defCoursesRoute } from '@course/components/courses-app-router';
import { LoadedProps } from '@components/router';
import { CommunityLayout, loadAipAccessState } from '../mirasee-community/community-layout';
import { CommunityHeader } from '../mirasee-community/community-header';
import { StudentNotifications } from 'client/courses/components/student-notifications';
import { UserMenu } from 'client/courses/components/nav/user-menu';
import { AIP_COURSE_ID } from 'shared/mirasee-consts';
import {
  IcoChat,
  IcoVideoCamera,
  IcoAcademicCap,
  IcoSparkles,
  IcoQuestion,
} from '@components/icons';

function FaqItem({ question, answer }: { question: string; answer: string }) {
  return (
    <details class="group border-b border-gray-200 py-4">
      <summary class="flex cursor-pointer items-center justify-between text-gray-900 font-medium">
        {question}
        <span class="ml-4 text-gray-400 group-open:rotate-45 transition-transform text-xl">+</span>
      </summary>
      <p class="mt-3 text-gray-600 text-sm leading-relaxed">{answer}</p>
    </details>
  );
}

async function load() {
  return loadAipAccessState();
}

function Page({ state }: LoadedProps<typeof load>) {
  const { isMember, hadMembership, pendingAccessDate } = state;

  return (
    <CommunityLayout
      selectedItem="help"
      isAipMember={isMember}
      hadMembership={hadMembership}
      pendingAccessDate={pendingAccessDate}
    >
      <CommunityHeader
        actions={
          <>
            {isMember && (
              <StudentNotifications course={{ id: AIP_COURSE_ID, title: 'AI Playground' }} />
            )}
            <UserMenu />
          </>
        }
      />
      <div class="max-w-3xl">
        <div class="flex items-center gap-3 mb-8">
          <span class="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-[#003DA6]/10 text-[#003DA6]">
            <IcoQuestion class="w-5 h-5" />
          </span>
          <h1 class="text-2xl font-semibold text-gray-900">Help & Support</h1>
        </div>

        <section class="mb-10">
          <h2 class="text-lg font-semibold text-gray-900 mb-4">Getting Started</h2>
          <p class="text-gray-600 mb-6">
            AI Playground is your home base for exploring what's possible with AI as a coach,
            consultant, or course creator. Here's a quick overview of what's available to you.
          </p>
          <div class="grid gap-4 sm:grid-cols-2">
            <div class="flex gap-3 p-4 rounded-lg border border-gray-200">
              <span class="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-lg bg-indigo-50 text-[#003DA6]">
                <IcoSparkles class="w-5 h-5" />
              </span>
              <div>
                <span class="font-medium text-gray-900 block text-sm">AI Playground Home</span>
                <span class="text-sm text-gray-500">
                  Your community feed with posts, announcements, and updates from the Mirasee team
                  and fellow members.
                </span>
              </div>
            </div>
            <div class="flex gap-3 p-4 rounded-lg border border-gray-200">
              <span class="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-lg bg-indigo-50 text-[#003DA6]">
                <IcoChat class="w-5 h-5" />
              </span>
              <div>
                <span class="font-medium text-gray-900 block text-sm">Discussions</span>
                <span class="text-sm text-gray-500">
                  Explore discussion topics, ask questions, share wins, and connect with the
                  community organized by category.
                </span>
              </div>
            </div>
            <div class="flex gap-3 p-4 rounded-lg border border-gray-200">
              <span class="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-lg bg-teal-50 text-[#00ABC8]">
                <IcoVideoCamera class="w-5 h-5" />
              </span>
              <div>
                <span class="font-medium text-gray-900 block text-sm">Calls</span>
                <span class="text-sm text-gray-500">
                  Join live calls with the Mirasee team. Upcoming calls and past recordings are all
                  available here.
                </span>
              </div>
            </div>
            <div class="flex gap-3 p-4 rounded-lg border border-gray-200">
              <span class="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-lg bg-amber-50 text-[#FF6B00]">
                <IcoAcademicCap class="w-5 h-5" />
              </span>
              <div>
                <span class="font-medium text-gray-900 block text-sm">Courses</span>
                <span class="text-sm text-gray-500">
                  Access your enrolled courses, training materials, and resources to help you
                  leverage AI in your business.
                </span>
              </div>
            </div>
          </div>
        </section>

        <section class="mb-10">
          <h2 class="text-lg font-semibold text-gray-900 mb-4">Frequently Asked Questions</h2>
          <div class="border-t border-gray-200">
            <FaqItem
              question="How do I join a live call?"
              answer="Navigate to 'Calls' in the sidebar. You'll see upcoming calls with their dates and times. When a call is live, a 'Join Now' button will appear. You'll also receive email reminders before each call."
            />
            <FaqItem
              question="Where can I find recordings of past calls?"
              answer="Go to the Calls page from the sidebar. Past calls are listed below upcoming ones, and each includes a link to watch the recording."
            />
            <FaqItem
              question="How do I start a discussion?"
              answer="From the AI Playground home page, use the post box at the top of the feed to write your post. You can also go to Discussions in the sidebar to browse and post in specific categories."
            />
            <FaqItem
              question="How do I access my courses?"
              answer="Click 'Courses' in the sidebar to see all courses you're enrolled in. From there, you can access lessons, materials, and any course-specific discussions."
            />
            <FaqItem
              question="Can I update my profile or account settings?"
              answer="Click 'Account' at the bottom of the sidebar to manage your profile, update your email, or change your password."
            />
            <FaqItem
              question="I'm having trouble logging in. What should I do?"
              answer="Try the 'Forgot your password?' link on the login page to reset your password. If you continue to have issues, contact our support team using the information below."
            />
          </div>
        </section>

        <section class="mb-10 p-6 rounded-lg bg-gray-50 border border-gray-200">
          <h2 class="text-lg font-semibold text-gray-900 mb-2">Contact Support</h2>
          <p class="text-gray-600 mb-4">
            Can't find what you're looking for? Our support team is here to help.
          </p>
          <a
            href="mailto:support@mirasee.com"
            class="inline-flex items-center gap-2 text-[#003DA6] font-medium hover:underline"
          >
            support@mirasee.com
          </a>
        </section>
      </div>
    </CommunityLayout>
  );
}

export const route = defCoursesRoute({
  Page,
  load,
  authLevel: 'student',
});
